<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div class="bna-compare">
    <figure class="bna-compare__after">
      <slot name="after">
        <picture>
          <source media="(min-width: 767px)" :srcset="afterImage.md" />
          <img :src="afterImageSource" :alt="afterImageAlt" width="100%" :loading="loading" />
        </picture>
      </slot>
      <slot name="after-caption">
        <figcaption v-if="!!afterLabel" v-text="afterLabel"></figcaption>
      </slot>
    </figure>

    <div class="bna-compare__overlay" :style="{ '--position': position + '%' }">
      <figure class="bna-compare__before">
        <slot name="before">
          <picture>
            <source media="(min-width: 767px)" :srcset="beforeImage.md" />
            <img :src="beforeImageSource" :alt="beforeImageAlt" width="100%" :loading="loading" />
          </picture>
        </slot>
        <slot name="before-caption">
          <figcaption v-if="!!beforeLabel" v-text="beforeLabel"></figcaption>
        </slot>
      </figure>

      <div class="bna-compare__handle-container">
        <div
          role="separator"
          class="bna-compare__handle"
          tabindex="0"
          :aria-label="buttonAriaLabel"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="position"
          @keydown="handleKeydown"
          @mousedown="handleMouseDown"
          @touchstart="handleTouchStart"
          @blur="isKeyboardSliding = false"
        >
          <slot name="handle"></slot>
        </div>
      </div>
    </div>

    <slot name="extra"></slot>
  </div>
</template>

<script>
import { dynamicCompareProps as props, defaultKeyboardStep } from './prop-definitions';

export default {
  name: 'refapp-beforeafter-compare',
  inject: [
    'beforeLabel',
    'afterLabel',
    'buttonAriaLabel',
    'loading',
    'keyboardStep',
    'initialPosition',
    'direction',
  ],
  props,
  data() {
    return {
      // percentage the after image being exposed
      position: Math.abs(100 - this.initialPosition),
      isPointerSliding: false,
      isKeyboardSliding: false,
    };
  },
  computed: {
    beforeImageSource() {
      return this.beforeImage?.sm || this.beforeImage.md;
    },

    afterImageSource() {
      return this.afterImage?.sm || this.afterImage.md;
    },
  },
  watch: {
    beforeImage() {
      this.position = Math.abs(100 - this.initialPosition);
    },
    isPointerSliding(val) {
      if (val) {
        this.dispatchSlideTaggingEvent();
      }
    },
    isKeyboardSliding(val) {
      if (val) {
        this.dispatchSlideTaggingEvent();
      }
    },
  },
  methods: {
    // set the slider position to a specific point, depending on layout direction
    setSliderPosition(point) {
      if (this.direction === 'horizontal') {
        this.setSliderPositionX(point.x);
      }
    },
    setSliderPositionX(pageX) {
      const imageBox = this.$el.getBoundingClientRect();
      const x = pageX - imageBox.left - window.scrollX;
      const newPosition = 100 - (x / imageBox.width) * 100;

      this.updatePositionValue(newPosition);
    },
    handleMouseMove(e) {
      this.setSliderPosition({
        x: e.pageX,
        y: e.pageY,
      });
      this.isPointerSliding = true;
    },
    handleTouchMove(e) {
      this.setSliderPosition({
        x: e.touches[0].pageX,
        y: e.touches[0].pageY,
      });
      this.isPointerSliding = true;
    },
    clearMouseMoveListener() {
      this.isPointerSliding = false;
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.clearMouseMoveListener);
    },
    clearTouchMoveListener() {
      this.isPointerSliding = false;
      window.removeEventListener('touchmove', this.handleTouchMove);
      window.removeEventListener('touchend', this.clearTouchMoveListener);
    },
    // when presing the mouse on the handle, add listener to track its movement
    // and another to remove these listeners once the mouse has been released
    handleMouseDown() {
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.clearMouseMoveListener);
    },
    // when starting to swipe on the handle, add listener to track the movement
    // and another to remove these listeners once the gesture has ended
    handleTouchStart() {
      window.addEventListener('touchmove', this.handleTouchMove);
      window.addEventListener('touchend', this.clearTouchMoveListener);
    },
    updatePositionValue(newValue) {
      // limit value between 0 and 100 so we don't lose the handle
      this.position = Math.max(0, Math.min(newValue, 100));
    },
    handleKeydown(e) {
      const slideKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!slideKeys.includes(e.key)) {
        return;
      }
      e.preventDefault();

      // this input may come as a string, parse it to Int
      let step = parseInt(this.keyboardStep, 10);
      step = isNaN(step) ? defaultKeyboardStep : step;

      let newPosition = this.position;
      const keyHandlers = {
        ArrowRight: () => (newPosition = this.position - step),
        ArrowLeft: () => (newPosition = this.position + step),
        Home: () => (newPosition = 100),
        End: () => (newPosition = 0),
      };
      keyHandlers[e.key]?.();

      this.updatePositionValue(newPosition);
      this.isKeyboardSliding = true;
    },
    dispatchSlideTaggingEvent() {
      const htmlEvent = new CustomEvent('bnaslide');
      this.$el.dispatchEvent(htmlEvent);
    },
  },
};
</script>
